/**
 * @tradeview/shared — the one place roles, stages and the awarded lifecycle are defined.
 *
 * The data lives in ../definitions.json so plain Node scripts (check-consistency.js,
 * the rules generator) and Cloud Functions can read the same file without a build.
 * This module adds the types and the derived lists.
 *
 * Rule (roadmap §2.5): add, never rename. A key that v1 still reads must not change
 * until v1 is retired.
 */
import defs from '../definitions.json';

export type RoleTier = 'office' | 'field';
export type Role = keyof typeof defs.roles;
export type BidStage = (typeof defs.bidStages)[number];
export type AwardedStage = (typeof defs.awardedLifecycle)[number];
export type Stage = BidStage | AwardedStage;
export type StagePhase = 'bid' | 'field' | 'closed';

export interface RoleDef { label: string; tier: RoleTier; restricted?: boolean }
export interface StageDef { label: string; phase: StagePhase }

export const DEFINITIONS_VERSION: number = defs.version;

/** Every role with its label and tier, in display order. */
export const ROLES = defs.roles as Record<Role, RoleDef>;
export const ROLE_IDS = Object.keys(ROLES) as Role[];

/** Bid stages in pipeline order; 'lost' is terminal. */
export const BID_STAGES = defs.bidStages as readonly BidStage[];
/** Field lifecycle in order, from award to closeout. */
export const AWARDED_LIFECYCLE = defs.awardedLifecycle as readonly AwardedStage[];
/** Label and phase for every stage id. */
export const STAGES = defs.stages as Record<Stage, StageDef>;

// ── Derived lists. Computed, never hand-typed (see definitions.json → derived). ──
export const OFFICE_ROLES = ROLE_IDS.filter(r => ROLES[r].tier === 'office');
export const FIELD_ROLES = ROLE_IDS.filter(r => ROLES[r].tier === 'field');
export const RESTRICTED_ROLES = ROLE_IDS.filter(r => ROLES[r].restricted);
export const ACTIVE_BID_STAGES = BID_STAGES.filter(s => s !== 'lost');
export const CAN_PROCESS_PO_ROLES: Role[] = [...OFFICE_ROLES, 'super', 'foreman'];
export const OFFICE_OR_SUPER_ROLES: Role[] = [...OFFICE_ROLES, 'super'];

// ── Gates. Same names and meaning as v1's index.html so the two apps agree. ──
export const isOffice = (role: string | null | undefined): boolean => OFFICE_ROLES.includes(role as Role);
export const isField = (role: string | null | undefined): boolean => FIELD_ROLES.includes(role as Role);
export const isAdmin = (role: string | null | undefined): boolean => role === 'admin';
export const isRestricted = (role: string | null | undefined): boolean => RESTRICTED_ROLES.includes(role as Role);
export const canSeeAdminPanel = (role: string | null | undefined): boolean =>
  isAdmin(role) || role === 'owner' || role === 'office_admin';
export const canSeeFinancials = (role: string | null | undefined): boolean => isOffice(role);
export const canSeePOCosts = (role: string | null | undefined): boolean => CAN_PROCESS_PO_ROLES.includes(role as Role);
export const isBidStage = (stage: string | null | undefined): stage is BidStage => BID_STAGES.includes(stage as BidStage);
export const isAwardedStage = (stage: string | null | undefined): stage is AwardedStage =>
  AWARDED_LIFECYCLE.includes(stage as AwardedStage);
export const stageLabel = (stage: string | null | undefined): string =>
  (STAGES as Record<string, StageDef>)[stage ?? '']?.label ?? String(stage ?? '');

/** Plan edit rights: office plus the two field leads, as in v1's PLAN_EDIT_ROLES. */
export const canEditPlan = (role: string | null | undefined): boolean =>
  isOffice(role) || role === 'super' || role === 'foreman';
